/**
 * useLiveSearch — debounced live-search hook for AgentSearch.
 *
 * When the user types in the morph palette, this hook fans out to
 * the host's `searchable[]` kinds + cross-app federation in
 * parallel via `runSearch`, returning sectioned hits plus a
 * `failedCount` of cross-app targets that timed out.
 *
 *   const live = useLiveSearch(query, config)
 *   // live.hostSections     → host-app entity sections
 *   // live.crossAppSections → "in other apps" sections
 *   // live.failedCount      → number of cross-app timeouts/errors
 *   // live.isLoading        → true while a query is in flight (debounce + fetch)
 *
 * Debounced 250ms to match the legacy host-app behavior. Empty
 * queries return empty results synchronously — no fetch fired.
 */

import { useEffect, useState } from 'react'

import type { AgentEmbedConfig } from '../types'
import { runSearch, type SearchRun } from './search-runner'

const DEBOUNCE_MS = 250

export interface LiveSearchResult extends SearchRun {
  /** True while a non-empty query has been entered but the fan-out
   *  hasn't resolved yet. Surfaces use this to render skeleton rows
   *  so users don't read an in-flight search as "no results". */
  isLoading: boolean
}

const EMPTY: LiveSearchResult = { hostSections: [], crossAppSections: [], failedCount: 0, isLoading: false }

export function useLiveSearch(query: string, config: AgentEmbedConfig | undefined): LiveSearchResult {
  const [run, setRun] = useState<LiveSearchResult>(EMPTY)

  useEffect(() => {
    if (!config) {
      setRun(EMPTY)
      return
    }
    const trimmed = query.trim()
    if (trimmed.length === 0) {
      setRun(EMPTY)
      return
    }
    const noKinds = config.searchable.length === 0 && (config.crossApp?.apps.length ?? 0) === 0
    if (noKinds) {
      setRun(EMPTY)
      return
    }

    // Mark loading immediately so consumers can render skeletons during
    // the debounce window AND the in-flight fetch.
    setRun((prev) => ({ ...prev, isLoading: true }))

    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const next = await runSearch(trimmed, config)
        if (!cancelled) {
          setRun({ ...next, isLoading: false })
        }
      } catch {
        if (!cancelled) {
          setRun(EMPTY)
        }
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, config])

  return run
}
